import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session, TestPlan } from '../../types';
import { apiRequest } from '../../utils/api';
import { formatDate } from '../../utils/date';
import { theme } from '../../theme';
import { MessageSetter, showTempMessage } from '../../utils/messages';
import { Icons } from '../../utils/icons';

interface SessionCardProps {
  session: Session;
  isSelected: boolean;
  onSelect: (session: Session | null) => void;
  onSessionChange: () => Promise<void>; // Called after start/delete to refresh list
  setMessage?: MessageSetter; // Optional message setter for error/success messages
  onEdit?: (sessionId: string) => void; // Optional callback to open edit modal
  onOpenTestPlan?: (testPlanUrl: string) => void; // Optional callback to open test plan modal
  milestones?: Record<string, { startDate: string; endDate?: string }>;
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  isSelected,
  onSelect,
  onSessionChange,
  setMessage,
  onEdit,
  onOpenTestPlan,
  milestones = {}
}) => {
  const navigate = useNavigate();
  const isCompleted = session.status === 'completed';
  const [testPlanName, setTestPlanName] = useState<string | null>(null);

  // Fetch test plan name when session has testPlanUrl
  useEffect(() => {
    if (session.testPlanUrl) {
      const testPlanIdMatch = session.testPlanUrl.match(/\/testplan\/([^/?]+)/);
      const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
      if (testPlanId) {
        const fetchTestPlanName = async () => {
          try {
            const testPlanData = await apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true });
            setTestPlanName(testPlanData.title);
          } catch (error) {
            console.error('Error fetching test plan name:', error);
            setTestPlanName(null);
          }
        };
        fetchTestPlanName();
      } else {
        setTestPlanName(null);
      }
    } else {
      setTestPlanName(null);
    }
  }, [session.testPlanUrl]);

  const handleStartSession = async () => {
    try {
      const freshSession = await apiRequest<Session>(`/api/sessions/${session.id}`, {}, { useAuth: true });
      
      if (freshSession.status === 'todo') {
        try {
          await apiRequest<Session>(`/api/sessions/${session.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              status: 'inprogress',
              startedAt: new Date().toISOString(),
              user: freshSession.user,
              subDepStatuses: (freshSession.subDependencies || []).map((subDepId: string) => ({
                subDepId,
                tested: false,
                bugged: false
              })),
              bugUrls: []
            })
          }, { useAuth: true, setMessage, successMessage: 'Session started successfully' });
        } catch (updateError) {
          if (updateError instanceof Error && updateError.message.includes('locked')) {
            if (setMessage) {
              showTempMessage(setMessage, { type: 'error', text: `Cannot start session: ${updateError.message}` });
            } else {
              alert(`Cannot start session: ${updateError.message}`);
            }
            await onSessionChange();
            return;
          }
          throw updateError;
        }
      } else if (freshSession.status === 'inprogress') {
        if (freshSession.activeUser && freshSession.activeUser !== freshSession.user) {
          if (setMessage) {
            showTempMessage(setMessage, { type: 'error', text: `This session is currently being used by ${freshSession.activeUser}. Only one user can run a session at a time.` });
          } else {
            alert(`This session is currently being used by ${freshSession.activeUser}. Only one user can run a session at a time.`);
          }
          await onSessionChange();
          return;
        }
      }
      
      navigate(`/session/${session.id}`);
    } catch (error) {
      console.error('Error starting session:', error);
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Failed to start session' });
      } else {
        alert('Failed to start session');
      }
    }
  };

  const handleDeleteSession = async () => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await apiRequest(`/api/sessions/${session.id}`, { method: 'DELETE' }, { useAuth: true, setMessage, successMessage: 'Session deleted successfully' });
      await onSessionChange();
    } catch (error) {
      console.error('Error deleting session:', error);
      // Error message already set by apiRequest
    }
  };

  const handleCardClick = () => {
    if (isSelected) {
      onSelect(null);
    } else {
      onSelect(session);
    }
  };

  return (
    <div
      className="card"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: isSelected ? `2px solid ${theme.cyanGlow}` : `1px solid ${theme.gridLine}`,
        backgroundColor: isSelected ? theme.cyanTransparent : theme.surface,
        boxShadow: isSelected ? theme.borderGlow : 'none',
        overflow: 'hidden',
        wordWrap: 'break-word',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '260px',
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = isSelected ? theme.borderGlow : '0 2px 4px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Content area - fixed structure */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {/* Line 1: ID with Status Badge */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              fontSize: '0.75rem',
              color: theme.textMuted,
              fontWeight: '600',
              fontFamily: 'monospace',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.2',
              height: '1.2em',
              flex: 1,
              minWidth: 0,
            }}
          >
            {session.id}
          </div>
          <span className={`status-badge status-${session.status}`} style={{ flexShrink: 0 }}>
            {session.status.toUpperCase()}
          </span>
        </div>

        {/* Line 2: Session Type */}
        <div
          style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: theme.textPrimary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            height: '1.2em',
          }}
        >
          {session.sessionType}
        </div>

        {/* Line 3: User | Platforms | Milestone (1 line) */}
        <div
          style={{
            color: theme.textSecondary,
            fontSize: '0.875rem',
            lineHeight: '1.2',
            height: '1.2em',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {session.user} |{' '}
          {session.platforms && session.platforms.length > 0
            ? session.platforms.join(', ')
            : session.platform || 'N/A'}
          {session.milestone && (() => {
            const milestoneMeta = milestones[session.milestone];
            const dateInfo = milestoneMeta 
              ? (milestoneMeta.endDate 
                  ? ` (${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')} - ${formatDate(milestoneMeta.endDate, 'MMM dd, yyyy')})`
                  : ` (${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')})`)
              : '';
            return ` | ${session.milestone}${dateInfo}`;
          })()}
          {session.activeUser && session.activeUser !== session.user && (
            <span style={{ color: theme.danger, fontWeight: '600', marginLeft: '0.5rem' }}>
              (Locked by {session.activeUser})
            </span>
          )}
        </div>

        {/* Line 5: Deadline */}
        <div style={{ lineHeight: '1.2', height: '1.2em', fontSize: '0.875rem', color: theme.textSecondary }}>
          <span style={{ color: theme.textMuted }}>Deadline: </span>
          {session.deadline ? formatDate(session.deadline, 'MMM dd, yyyy') : ''}
        </div>

        {/* Line 6: Test Plan */}
        <div style={{ lineHeight: '1.2', height: '1.2em', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
          <span style={{ color: theme.textMuted, flexShrink: 0 }}>Test Plan: </span>
          {session.testPlanUrl ? (() => {
            const testPlanIdMatch = session.testPlanUrl.match(/\/testplan\/([^/?]+)/);
            const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
            const displayText = testPlanName || session.testPlanUrl;
            
            const handleOpenTestPlan = () => {
              if (onOpenTestPlan && session.testPlanUrl) {
                onOpenTestPlan(session.testPlanUrl);
              }
            };
            
            return testPlanId ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenTestPlan();
                }}
                style={{
                  color: theme.cyanGlow,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flex: 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                title={displayText}
              >
                {displayText}
              </button>
            ) : (
              <a
                href={session.testPlanUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: theme.cyanGlow,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flex: 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                title={session.testPlanUrl}
              >
                {session.testPlanUrl}
              </a>
            );
          })() : (
            <span style={{ fontSize: '0.875rem', color: theme.textSecondary }}></span>
          )}
        </div>

        {/* Line 7: Reference */}
        <div style={{ 
          lineHeight: '1.2', 
          height: '1.2em', 
          fontSize: '0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          minWidth: 0
        }}>
          <span style={{ color: theme.textMuted, flexShrink: 0 }}>Reference: </span>
          {session.referenceUrl ? (
            <a
              href={session.referenceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                color: theme.cyanGlow,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1,
                minWidth: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              {session.referenceUrl}
            </a>
          ) : null}
        </div>

        {/* Line 8: Notes */}
        <div style={{ 
          lineHeight: '1.2', 
          height: '1.2em', 
          fontSize: '0.875rem', 
          color: theme.textSecondary,
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          minWidth: 0
        }}>
          <span style={{ color: theme.textMuted, flexShrink: 0 }}>Notes: </span>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
          >
            {session.notes || ''}
          </span>
        </div>
      </div>

      {/* Footer - always at bottom */}
      {isCompleted ? (
        <div style={{ marginTop: 'auto', paddingTop: '0.1rem', fontSize: '0.875rem', color: theme.textSecondary }}>
          {isSelected ? 'Click to hide details' : 'Click to view details'}
        </div>
      ) : (
        <div style={{ marginTop: 'auto', paddingTop: '0.1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              handleStartSession();
            }}
            style={{ flex: 1 }}
          >
            {session.status === 'todo' ? 'Start Session' : 'Continue Session'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              if (onEdit) {
                onEdit(session.id);
              } else {
                navigate(`/edit-session/${session.id}`);
              }
            }}
            style={{
              padding: '0.5rem',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Edit session"
          >
            ✏️
          </button>
          <button
            className="btn btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSession();
            }}
            style={{
              padding: '0.5rem',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Delete session"
          >
            {Icons.delete}
          </button>
        </div>
      )}

    </div>
  );
};

export default SessionCard;
